package features.timeoff.handlers

import AppState
import Router
import features.timeoff.data.TimeOffRepository
import features.notifications.data.NotificationRepository
import kotlinx.browser.window

/**
 * Time Off Request Action Handlers
 * Handles approve/reject actions with notification integration
 */

fun approveTimeOffRequest(teamId: String, requestId: String) {
    console.log("✅ Approving time off request: $requestId for team: $teamId")
    
    val reviewerId = AppState.currentUser?.id
    val reviewerName = AppState.currentUser?.name
    if (reviewerId == null) {
        window.alert("User not authenticated")
        return
    }
    
    // Show confirmation
    val confirmed = window.confirm("Approve this time off request?")
    if (!confirmed) return
    
    // First, get the time off request to find the requester
    TimeOffRepository.getTimeOffRequestById(teamId, requestId)
        .then { request ->
            if (request == null) {
                window.alert("Time off request not found")
                return@then
            }
            
            // Approve the request
            TimeOffRepository.approveRequest(teamId, requestId, reviewerId)
                .then {
                    console.log("✅ Time off request approved successfully")
                    
                    // Format date range for notification
                    val dateRange = TimeOffRepository.formatDateRange(request.startDate, request.endDate)
                    
                    // Create notification for requester
                    val notificationTitle = "Time Off Request Approved ✅"
                    val notificationMessage = "Your time off request for $dateRange (${request.days} days) has been approved by ${reviewerName ?: "admin"}."
                    
                    NotificationRepository.createNotification(
                        userId = request.userId,
                        type = "timeoff_approved",
                        title = notificationTitle,
                        message = notificationMessage,
                        relatedId = requestId
                    ).then {
                        console.log("✅ Notification sent to requester")
                        window.alert("Time off request approved!")
                        Router.render()
                    }.catch { error ->
                        console.error("❌ Failed to send notification:", error)
                        window.alert("Time off request approved, but failed to send notification.")
                        Router.render()
                    }
                }
                .catch { error ->
                    console.error("❌ Failed to approve time off request:", error)
                    window.alert("Failed to approve request. Please try again.")
                }
        }
        .catch { error ->
            console.error("❌ Failed to get time off request:", error)
            window.alert("Failed to load request details. Please try again.")
        }
}

fun rejectTimeOffRequest(teamId: String, requestId: String) {
    console.log("❌ Rejecting time off request: $requestId for team: $teamId")
    
    val reviewerId = AppState.currentUser?.id
    val reviewerName = AppState.currentUser?.name
    if (reviewerId == null) {
        window.alert("User not authenticated")
        return
    }
    
    // Show confirmation
    val confirmed = window.confirm("Reject this time off request?")
    if (!confirmed) return
    
    // First, get the time off request to find the requester
    TimeOffRepository.getTimeOffRequestById(teamId, requestId)
        .then { request ->
            if (request == null) {
                window.alert("Time off request not found")
                return@then
            }
            
            // Reject the request
            TimeOffRepository.rejectRequest(teamId, requestId, reviewerId)
                .then {
                    console.log("✅ Time off request rejected successfully")
                    
                    // Format date range for notification
                    val dateRange = TimeOffRepository.formatDateRange(request.startDate, request.endDate)
                    
                    // Create notification for requester
                    val notificationTitle = "Time Off Request Rejected ❌"
                    val notificationMessage = "Your time off request for $dateRange (${request.days} days) has been rejected by ${reviewerName ?: "admin"}."
                    
                    NotificationRepository.createNotification(
                        userId = request.userId,
                        type = "timeoff_rejected",
                        title = notificationTitle,
                        message = notificationMessage,
                        relatedId = requestId
                    ).then {
                        console.log("✅ Notification sent to requester")
                        window.alert("Time off request rejected!")
                        Router.render()
                    }.catch { error ->
                        console.error("❌ Failed to send notification:", error)
                        window.alert("Time off request rejected, but failed to send notification.")
                        Router.render()
                    }
                }
                .catch { error ->
                    console.error("❌ Failed to reject time off request:", error)
                    window.alert("Failed to reject request. Please try again.")
                }
        }
        .catch { error ->
            console.error("❌ Failed to get time off request:", error)
            window.alert("Failed to load request details. Please try again.")
        }
}
